'use client'

import AutomationTemplate from '@/components/DashboardV2/AutomationTemplate'
import { Skeleton } from '@/components/ui/skeleton'
import { Spinner } from '@/components/ui/spinner'
import { getCopilotService } from '@/lib/features/copilotv2/CopilotService'
import { useGetUserQuery } from '@/lib/features/user/userApi'
import {
  useDeleteWorkflowMutation,
  useGetWorkspaceTagsQuery,
  useGetWorkspaceWorkflowMetadataQuery,
  useSetWorkflowBookmarkMutation,
  useUpdateWorkflowMutation,
} from '@/lib/features/workflow/workflowApi'
import { PAGE_PATH } from '@/lib/page-paths'
import { formatWorkflowName } from '@/utils/workflowUtils'
import { AnimatePresence, motion } from 'framer-motion'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { useCallback, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import AppSearchToolbar, { SortMode, SortOrder } from '../Dashboard/AppSearchToolbar'
import CategoryItem from '../Dashboard/CategoryItem'
import NoResultsFound from '../Dashboard/NoResultsFound'
import ShowMoreButton from '../Dashboard/ShowMoreButton'

const MAX_CARDS = 9 // Define constant here

export default function FavoriteAutomations() {
  const [showAll, setShowAll] = useState(false)
  const [searchValue, setSearchValue] = useState<string>('')
  const [totalLimit, setTotalLimit] = useState(20)
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([])
  const [tagDropdownState, setTagDropdownState] = useState<{
    workflowId: string | null
    position: { x: number; y: number } | null
  }>({
    workflowId: null,
    position: null,
  })
  const router = useRouter()

  const { data: userData } = useGetUserQuery()
  const workspaceId = userData?.workspace?.id

  const { data: workspaceTags = [] } = useGetWorkspaceTagsQuery(workspaceId, {
    skip: !workspaceId,
  })

  // Use a single query with increasing limit to get all data at once
  const {
    data: workflowData,
    isError: isWorkflowsError,
    isLoading: isLoadingWorkflows,
    isFetching: isFetchingWorkflows,
  } = useGetWorkspaceWorkflowMetadataQuery({ limit: totalLimit, offset: 0, bookmarked: true })

  // Use all workflows from the single query
  const allWorkflows = useMemo(() => {
    return workflowData?.workflows || []
  }, [workflowData?.workflows])

  const [deleteWorkflow] = useDeleteWorkflowMutation()
  const [updateWorkflow] = useUpdateWorkflowMutation()
  const [setWorkflowBookmark] = useSetWorkflowBookmarkMutation()
  const { createSession } = getCopilotService()

  // Load more workflows functionality
  const loadMoreWorkflows = useCallback(() => {
    if (workflowData?.pagination?.hasMore && !isFetchingWorkflows) {
      setTotalLimit((prev) => prev + 20)
    }
  }, [workflowData?.pagination?.hasMore, isFetchingWorkflows])

  // Reset limit when search changes to start fresh
  const resetSearch = useCallback(() => {
    setTotalLimit(20)
  }, [])

  // Use all workflows as the source of truth
  const workflowMetadataList = allWorkflows
  const pagination = workflowData?.pagination

  const availableSortModes: SortMode[] = useMemo(
    () => ['Last Edited', 'Alphabetical', 'Created'],
    []
  )
  const [activeSortMode, setActiveSortMode] = useState<SortMode>('Last Edited')
  const [activeSortOrder, setActiveSortOrder] = useState<SortOrder | null>(
    'Descending' // Default for 'Last Edited'
  )

  const handleSortModeChange = (newMode: SortMode) => {
    setActiveSortMode(newMode)
    if (newMode === 'Default') {
      setActiveSortOrder(null)
    } else if (newMode === 'Alphabetical') {
      setActiveSortOrder('Ascending')
    } else if (['Last Edited', 'Created'].includes(newMode)) {
      setActiveSortOrder('Descending')
    }
  }

  const filteredAndSortedWorkflows = useMemo(() => {
    const workflows = workflowMetadataList || []
    // Filter by search value (bookmarked filter is already applied server-side)
    let filteredWorkflows = workflows.filter((workflow) =>
      workflow.name?.toLowerCase().includes(searchValue.toLowerCase())
    )

    // Filter by selected tags
    if (selectedTagIds.length > 0) {
      filteredWorkflows = filteredWorkflows.filter((workflow) => {
        const workflowTagIds = workflow.tags?.map((tag) => tag.id) || []
        return selectedTagIds.every((tagId) => workflowTagIds.includes(tagId))
      })
    }

    // Sort by the selected sort mode
    filteredWorkflows.sort((a, b) => {
      // Apply user-selected sorting
      if (activeSortMode !== 'Default' && activeSortOrder) {
        let compareResult = 0
        if (activeSortMode === 'Alphabetical') {
          compareResult = (a.name || 'Untitled').localeCompare(b.name || 'Untitled')
        } else if (activeSortMode === 'Last Edited') {
          compareResult = new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
        } else if (activeSortMode === 'Created') {
          compareResult = new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        }

        if (activeSortOrder === 'Ascending') {
          return activeSortMode === 'Alphabetical' ? compareResult : -compareResult
        } else {
          return activeSortMode === 'Alphabetical' ? -compareResult : compareResult
        }
      }

      return 0
    })

    return filteredWorkflows
  }, [activeSortMode, activeSortOrder, searchValue, selectedTagIds, workflowMetadataList])

  // Determine how many cards to show before 'Show More'
  const projectsToShow = showAll
    ? filteredAndSortedWorkflows
    : filteredAndSortedWorkflows.slice(0, MAX_CARDS)

  // Since we filter for bookmarked workflows client-side, we need to keep loading
  // more pages from the server to find more favorites
  const hasActiveFilters = searchValue !== '' || selectedTagIds.length > 0
  const canShowMore =
    filteredAndSortedWorkflows.length > MAX_CARDS ||
    (pagination?.hasMore && !hasActiveFilters && showAll)
  const canLoadMoreFromServer = pagination?.hasMore && !hasActiveFilters && showAll

  // No card click: use hrefs to /workflow/[id] for run

  const handleDuplicateWorkflow = useCallback(
    (workflowId: string) => {
      return async () => {
        if (!workspaceId) {
          console.error('WorkspaceId not found')
          return
        }

        try {
          const session = await createSession({
            workspaceId: workspaceId,
            workflowRef: workflowId,
            cloneWorkflow: true,
            source: 'favorite_automation',
          })

          const sessionId = session.session.id
          if (sessionId) {
            router.push(`${PAGE_PATH.COPILOT}/${sessionId}?source=workflow`)
          }
        } catch (error) {
          console.error('Failed to duplicate workflow:', error)
        }
      }
    },
    [workspaceId, createSession, router]
  )

  const handleDeleteWorkflow = useCallback(
    (workflowId: string) => async () => {
      try {
        await deleteWorkflow(workflowId).unwrap()
        toast.success('Workflow deleted successfully')
      } catch (error) {
        console.error('Failed to delete workflow:', error)
        toast.error('Failed to delete workflow. Please try again.')
      }
    },
    [deleteWorkflow]
  )

  const handleRenameWorkflow = useCallback(
    (workflowId: string) => {
      return async (newName: string) => {
        try {
          await updateWorkflow({
            workflowRef: workflowId,
            config: { name: newName },
          }).unwrap()
          toast.success('Workflow renamed successfully')
        } catch (error: any) {
          console.error('Failed to rename workflow:', error)
          const errorMessage =
            error?.data?.error || error?.error || 'Failed to rename workflow. Please try again.'
          toast.error(errorMessage)
        }
      }
    },
    [updateWorkflow]
  )

  const handleTagToggle = useCallback((tagId: string) => {
    setSelectedTagIds((prev) => {
      if (prev.includes(tagId)) {
        return prev.filter((id) => id !== tagId)
      }
      return [...prev, tagId]
    })
  }, [])

  if (isWorkflowsError) {
    return (
      <div className="container mx-auto flex flex-col px-3 md:px-10">
        <p className="text-red-500">Failed to load favorite automations. Please try again later.</p>
      </div>
    )
  }

  return (
    <div className="container mx-auto flex flex-col px-3 md:px-10">
      <div className="mb-2 mt-2 flex w-full flex-col items-start gap-2 md:flex-row md:items-center md:justify-between">
        <p
          className="text-left text-xl text-gray-900 md:text-2xl"
          style={{ fontFamily: 'Space Grotesk, sans-serif', letterSpacing: '0.01em' }}
        >
          Favorite Automations
        </p>
        {isFetchingWorkflows && (
          <div className="hidden items-center gap-2 md:flex">
            <Spinner variant="simple" size="sm" />
            <span className="text-sm text-gray-500">Refreshing…</span>
          </div>
        )}
        <div className="flex w-full md:w-auto">
          <AppSearchToolbar
            categories={[]}
            searchValue={searchValue}
            onSearchChange={(value) => {
              setSearchValue(value)
              if (value !== searchValue) {
                resetSearch()
              }
            }}
            availableSortModes={availableSortModes}
            activeSortMode={activeSortMode}
            activeSortOrder={activeSortOrder}
            onSortModeChange={handleSortModeChange}
            onSortOrderChange={setActiveSortOrder}
          />
        </div>
      </div>

      {workspaceTags.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 pb-4">
          <span className="text-sm font-medium text-gray-700">Filter by tags:</span>
          {workspaceTags.map((tag) => (
            <CategoryItem
              key={tag.id}
              category={tag.name}
              active={selectedTagIds.includes(tag.id)}
              onSelect={() => handleTagToggle(tag.id)}
              className="h-[32px]"
            />
          ))}
          {selectedTagIds.length > 0 && (
            <button
              type="button"
              onClick={() => setSelectedTagIds([])}
              className="text-sm text-gray-500 underline hover:text-gray-700"
            >
              Clear all
            </button>
          )}
        </div>
      )}

      {isLoadingWorkflows ? (
        <ul className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: MAX_CARDS }).map((_, idx) => (
            <li key={`workflow-skel-${idx}`}>
              <div className="relative h-40 w-full">
                <div className="absolute inset-0 rounded-2xl bg-white/60 backdrop-blur-xl" />
                <div className="relative z-10 flex h-full w-full items-center px-6 py-1">
                  <div className="mr-6 h-24 w-24 flex-shrink-0 overflow-hidden rounded-full">
                    <Skeleton className="h-full w-full" />
                  </div>
                  <div className="flex h-full min-w-0 flex-1 flex-col justify-center">
                    <Skeleton className="mb-2 h-5 w-2/3" />
                    <Skeleton className="mb-1 h-4 w-full" />
                    <Skeleton className="mb-3 h-4 w-5/6" />
                    <div className="flex items-center justify-between pb-2 pt-1">
                      <Skeleton className="h-3 w-24" />
                      <Skeleton className="h-6 w-6 rounded-full" />
                    </div>
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <>
          {/* No workflows at all */}
          {!hasActiveFilters && filteredAndSortedWorkflows.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <p className="text-lg text-gray-600">No favorite automations yet</p>
              <p className="mt-2 text-sm text-gray-500">
                Mark automations as favorites to see them here
              </p>
              <Link
                href={PAGE_PATH.AUTOMATIONS}
                className="mt-4 rounded-2xl border border-gray-200 bg-white px-6 py-3 text-sm font-medium text-gray-900 shadow-sm transition-colors hover:bg-gray-50"
              >
                Browse Automations
              </Link>
            </div>
          )}

          {/* Search with no results */}
          {searchValue !== '' &&
            selectedTagIds.length === 0 &&
            filteredAndSortedWorkflows.length === 0 && (
              <NoResultsFound message="No favorite automations match your search" />
            )}

          {/* Tag filter with no results */}
          {selectedTagIds.length > 0 && filteredAndSortedWorkflows.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <p className="text-lg text-gray-600">
                No favorite automations found with selected tags
              </p>
              <p className="mt-2 text-sm text-gray-500">Try adjusting your tag filters</p>
            </div>
          )}

          {filteredAndSortedWorkflows.length > 0 && (
            <ul className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
              <AnimatePresence mode="popLayout">
                {projectsToShow.map((workflow) => (
                  <motion.li
                    key={workflow.id}
                    layout
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                  >
                    <AutomationTemplate
                      id={workflow.id}
                      title={formatWorkflowName(workflow.name)}
                      description={workflow.description || ''}
                      updatedAt={workflow.updatedAt}
                      href={`${PAGE_PATH.WORKFLOW}/${workflow.id}`}
                      onDuplicateAction={handleDuplicateWorkflow(workflow.id)}
                      onDeleteAction={handleDeleteWorkflow(workflow.id)}
                      onRenameAction={handleRenameWorkflow(workflow.id)}
                      isBookmarked={workflow.isBookmarked}
                      onBookmarkToggle={async () => {
                        await setWorkflowBookmark({
                          workflowId: workflow.id,
                          bookmarked: !workflow.isBookmarked,
                        }).unwrap()
                      }}
                      hideBookmarkButton={true}
                      tags={workflow.tags}
                      onEditTags={(e: React.MouseEvent) => {
                        setTagDropdownState({
                          workflowId: workflow.id,
                          position: { x: e.clientX, y: e.clientY },
                        })
                      }}
                      isTagDropdownOpen={tagDropdownState.workflowId === workflow.id}
                      workspaceId={workflow.workspaceId}
                      onCloseTagDropdown={() => {
                        setTagDropdownState({ workflowId: null, position: null })
                      }}
                      dropdownPosition={
                        tagDropdownState.workflowId === workflow.id
                          ? tagDropdownState.position
                          : null
                      }
                      requiredIntegrations={workflow.requiredIntegrations}
                    />
                  </motion.li>
                ))}
              </AnimatePresence>
            </ul>
          )}

          {canShowMore && (
            <ShowMoreButton
              showAll={showAll}
              canLoadMore={canLoadMoreFromServer}
              onClick={() => {
                if (!showAll) {
                  setShowAll(true)
                } else if (canLoadMoreFromServer) {
                  loadMoreWorkflows()
                } else {
                  setShowAll(false)
                }
              }}
            />
          )}

          {/* Show loading indicator when fetching more workflows */}
          {canLoadMoreFromServer && isFetchingWorkflows && (
            <div className="mt-4 flex justify-center py-4">
              <Spinner variant="default" size={24} />
              <span className="ml-2 text-sm text-gray-600">Loading more favorites...</span>
            </div>
          )}

          {/* Show favorite workflows count */}
          {filteredAndSortedWorkflows.length > 0 && (
            <div className="mt-4 text-center text-sm text-gray-500">
              {hasActiveFilters ? (
                <>
                  Showing {projectsToShow.length} of {filteredAndSortedWorkflows.length} matching
                  favorites
                </>
              ) : (
                <>
                  {projectsToShow.length} favorite automation
                  {projectsToShow.length !== 1 ? 's' : ''}
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}
